import { defineStore } from "pinia"
import { computed, ref, shallowRef, triggerRef, type ComputedRef, type Ref } from "vue"
import { Instruction } from "@/models/instruction.model"
import { useOrderStore } from "@/store/orders.store"
import { storeToRefs } from "pinia"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import { type Moment } from "moment"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import { InstructionFeedback } from "@/models/instruction-feedback.model"
import type InstructionCardWithContext from "@/components/instruction/InstructionCardWithContext.vue"
import type LayoutColumn from "@/components/layout/LayoutColumn.vue"

export const useInstructionStore = defineStore("instructionStore", () => {
  const { orders } = storeToRefs(useOrderStore())

  const instructionListItemWithContextMenu: Ref<Instruction | null> = ref(null)
  const instructionCardWithContextMenu: Ref<Instruction | null> = ref(null)

  function addFeedbackToInstruction(instructionFeedback: InstructionFeedback) {
    const instruction = findInstructionById(instructionFeedback.instruction)
    if (instruction) {
      instruction.feedback = instructionFeedback
    }
  }

  function removeFeedbackFromInstruction(instructionFeedback: InstructionFeedback) {
    const instruction = instructions.value.find(
      (instruction) => instruction.feedback?.id === instructionFeedback.id
    )
    if (instruction) {
      delete instruction.feedback
    }
  }

  useSocketSynchronizationHandlers(
    InstructionFeedback,
    null,
    addFeedbackToInstruction,
    removeFeedbackFromInstruction,
    false // Do not ignore messages from self (mostly for testing purposes, but it does not hurt anyway)
  )

  const instructionWithContextMenu = computed(() => {
    return instructionListItemWithContextMenu.value || instructionCardWithContextMenu.value
  })

  const instructions: ComputedRef<Instruction[]> = computed(() => {
    const instructions = []
    for (const order of orders.value) {
      if (order.instructions) {
        instructions.push(...order.instructions)
      }
    }
    return instructions
  })
  const instructionsById: ComputedRef<Map<string, Instruction>> = computed(() => {
    const result = new Map<string, Instruction>()
    for (const instruction of instructions.value) {
      if (instruction.id) {
        result.set(instruction.id, instruction)
      }
    }
    return result
  })

  const instructionFeedbacks: ComputedRef<InstructionFeedback[]> = computed(() => {
    return instructions.value
      .map((instruction) => instruction.feedback)
      .filter((feedback): feedback is InstructionFeedback => !!feedback)
  })

  const instructionsForServiceProviderOnDate: Ref<{
    [key: string]: Instruction[]
  }> = ref({})

  function getInstructionsForServiceProviderAndDate(
    serviceProvider: KnownBusinessEntityContact,
    date: Moment
  ) {
    return instructions.value.filter(
      (instruction) =>
        instruction.serviceProvider?.id === serviceProvider.id && instruction.date?.isSame(date)
    )
  }

  function findInstructionById(id: string) {
    return instructions.value.find((instruction) => instruction.id === id)
  }

  // Registering expanded instructions per column.
  const expandedInstructionsPerColumn = shallowRef(
    new Map<string, Array<typeof InstructionCardWithContext>>()
  )
  function registerInstructionCardExpanded(
    column: typeof LayoutColumn,
    instructionCard: typeof InstructionCardWithContext
  ) {
    if (!expandedInstructionsPerColumn.value.has(column.uid)) {
      expandedInstructionsPerColumn.value.set(column.uid, [])
    }
    const existing = expandedInstructionsPerColumn.value.get(column.uid)!
    if (existing.length > 0) {
      const snapshot = existing.slice()
      for (const previouslyExpanded of snapshot) {
        if (previouslyExpanded !== instructionCard) {
          // forceCollapse may be missing on older / non-context variants of
          // the card (e.g. hot-reload races) — fall back to dropFocus.
          const exposed = previouslyExpanded.exposed
          if (typeof exposed.forceCollapse === "function") {
            exposed.forceCollapse()
          } else {
            exposed.dropFocus()
          }
        }
      }
    }
    expandedInstructionsPerColumn.value.get(column.uid)?.push(instructionCard)
    triggerRef(expandedInstructionsPerColumn)
  }
  function unregisterInstructionCardExpanded(
    column: typeof LayoutColumn | undefined,
    instructionCard: typeof InstructionCardWithContext
  ) {
    if (!column) {
      return // In case of hot reload, column might be undefined
    }
    const instructionCards = expandedInstructionsPerColumn.value.get(column.uid)
    if (!instructionCards) {
      return
    }
    const index = instructionCards.indexOf(instructionCard)
    if (index !== -1) {
      instructionCards.splice(index, 1)
      triggerRef(expandedInstructionsPerColumn)
    }
  }

  function hasExpandedInstructions(column?: typeof LayoutColumn) {
    return expandedInstructionsCount(column) > 0
  }

  function instructionIsExpanded(id: string, column: typeof LayoutColumn) {
    const instructionCards = expandedInstructionsPerColumn.value.get(column.uid)
    if (!instructionCards) {
      return false
    }
    return instructionCards.some((instructionCard) => {
      // Sometimes modelValue needs to be unpacked, sometimes not.
      const modelValue =
        instructionCard.exposed.modelValue.value ?? instructionCard.exposed.modelValue
      return modelValue.id === id
    })
  }

  /**
   * Counts the number of expanded instructions for a given column.
   * If no column is provided, it counts all expanded instructions across all columns.
   * @param column The column element to count expanded instructions for.
   * @return The count of expanded instructions for the given column or across all columns.
   */
  function expandedInstructionsCount(column?: typeof LayoutColumn): number {
    if (!column) {
      let count = 0
      for (const [, value] of expandedInstructionsPerColumn.value) {
        count += value.length
      }
      return count
    }
    const expandedInstructionsPerColumnInColumn = expandedInstructionsPerColumn.value.get(
      column.uid
    )
    return expandedInstructionsPerColumnInColumn ? expandedInstructionsPerColumnInColumn.length : 0
  }

  function collapseInstructions(column: typeof LayoutColumn) {
    const expandedInstructions = expandedInstructionsPerColumn.value.get(column.uid)
    if (expandedInstructions) {
      for (const instructionCard of [...expandedInstructions]) {
        const exposed = instructionCard.exposed
        if (typeof exposed.forceCollapse === "function") {
          exposed.forceCollapse()
        } else {
          exposed.dropFocus()
        }
      }
    }
  }

  return {
    instructionListItemWithContextMenu,
    instructionCardWithContextMenu,
    instructions,
    instructionsById,
    instructionsForServiceProviderOnDate,
    instructionFeedbacks,
    instructionWithContextMenu,
    getInstructionsForServiceProviderAndDate,
    registerInstructionCardExpanded,
    unregisterInstructionCardExpanded,
    hasExpandedInstructions,
    instructionIsExpanded,
    collapseInstructions,
    expandedInstructionsPerColumn
  }
})
